import React from 'react';
import { Sparkles, ArrowRight, ShieldCheck, RefreshCw, Ruler, Award, Heart } from 'lucide-react';

interface HeroProps {
  onExploreClick: () => void;
  onOpenSizingModal: () => void;
  onOpenCustomOrderModal: () => void;
}

export const Hero: React.FC<HeroProps> = ({
  onExploreClick,
  onOpenSizingModal,
  onOpenCustomOrderModal,
}) => {
  return (
    <section className="relative overflow-hidden pt-6 pb-16 md:pt-12 md:pb-24 pastel-iridescent-bg">
      {/* Background Floating Sparkles */}
      <div className="absolute top-12 left-10 text-2xl text-[#D4AF37] opacity-60 animate-sparkle-float select-none">
        ✦
      </div>
      <div className="absolute top-1/3 right-12 text-3xl text-[#F472B6] opacity-70 animate-sparkle-float-delayed select-none">
        ✨
      </div>
      <div className="absolute bottom-12 left-1/4 text-xl text-[#2DD4BF] opacity-60 animate-sparkle-float select-none">
        ✧
      </div>
      <div className="absolute top-20 right-1/4 text-2xl text-[#C084FC] opacity-70 animate-sparkle-float-delayed select-none">
        ✦
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Headline, Bio & CTAs */}
          <div className="lg:col-span-7 text-center lg:text-left space-y-6">
            
            {/* Pill Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-[#F3C053]/40 shadow-xs">
              <span className="flex h-2 w-2 rounded-full bg-[#10B981] animate-ping" />
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs sm:text-sm font-semibold text-[#78350F]">
                Hand-Painted by 14-Year-Old Gymnast & Artist Kylee
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="font-serif text-4xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight text-[#292524] leading-[1.15]">
              Salon-Quality Press-Ons with{' '}
              <span className="gold-gradient-text block sm:inline font-extrabold">
                Gymnast-Grade Durability
              </span>
            </h1>

            {/* Subtext */}
            <p className="text-base sm:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto lg:mx-0 font-normal">
              Skip the \$120 salon appointments and chemical damage. Kylee hand-paints each 10-piece luxury gel set with the same discipline, precision, and flair she brings to the gymnastics mat. Reusable, damage-free, and custom-sized to your hands.
            </p>

            {/* Price Highlight Banner */}
            <div className="inline-flex items-baseline gap-2 bg-gradient-to-r from-[#FEF08A]/60 via-[#FCE7F3]/60 to-[#EDE9FE]/60 px-4 py-2 rounded-2xl border border-[#FDE047]/60">
              <span className="text-xs uppercase font-bold tracking-wider text-[#854D0E]">Standard Price:</span>
              <span className="text-2xl sm:text-3xl font-extrabold text-[#B45309] font-serif">$30.00</span>
              <span className="text-xs text-[#78716C] font-medium">per full set + prep kit included</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
              <button
                onClick={onExploreClick}
                className="w-full sm:w-auto px-8 py-4 rounded-2xl text-white font-bold text-base gold-shimmer-btn shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group transition-transform active:scale-98"
              >
                <span>Shop $30 Nail Sets</span>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>

              <button
                onClick={onOpenSizingModal}
                className="w-full sm:w-auto px-6 py-4 rounded-2xl bg-white hover:bg-[#FAF5EE] text-[#44403C] hover:text-[#B45309] font-semibold text-base border border-[#E7DEC8] hover:border-[#D4AF37] shadow-xs flex items-center justify-center gap-2 transition-all active:scale-98"
              >
                <Ruler className="w-5 h-5 text-[#D4AF37]" />
                <span>Interactive Sizing Guide</span>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 border-t border-[#E8DFC8]/60 text-left">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-white shadow-xs border border-[#FDE68A]">
                  <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-[#292524]">100% Hand-Painted</h4>
                  <p className="text-[11px] text-[#78716C]">Layered UV Gel</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-white shadow-xs border border-[#FBCFE8]">
                  <RefreshCw className="w-4 h-4 text-[#EC4899]" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-[#292524]">Reusable 5+ Times</h4>
                  <p className="text-[11px] text-[#78716C]">Zero Nail Damage</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-white shadow-xs border border-[#DDD6FE]">
                  <ShieldCheck className="w-4 h-4 text-[#8B5CF6]" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-[#292524]">Gymnast Tested</h4>
                  <p className="text-[11px] text-[#78716C]">Extra Strong Apex</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-white shadow-xs border border-[#99F6E4]">
                  <Award className="w-4 h-4 text-[#0D9488]" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-[#292524]">Prep Kit Included</h4>
                  <p className="text-[11px] text-[#78716C]">Glue, Tabs, Buffer</p>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Hero Visual Logo Card with Holographic Halo */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-md">
              
              {/* Outer Decorative Gradient Rings */}
              <div className="absolute -inset-4 rounded-full bg-gradient-to-tr from-[#FDE68A] via-[#F472B6] to-[#67E8F9] opacity-40 blur-2xl animate-pulse" style={{ animationDuration: '4s' }} />
              
              {/* Main Card with Logo Display */}
              <div className="relative rounded-3xl bg-white/90 backdrop-blur-xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(212,175,55,0.2)] border-2 border-[#F3C053]/40 flex flex-col items-center text-center">
                
                {/* Logo Badge Container */}
                <div className="relative w-64 h-64 sm:w-72 sm:h-72 rounded-full p-2 bg-gradient-to-tr from-[#D4AF37] via-[#F472B6] to-[#2DD4BF] shadow-lg group">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center p-3 overflow-hidden transition-transform duration-500 group-hover:scale-103">
                    <img
                      src="/logo.png"
                      alt="Nailed It - Artistic Nails by Kylee Official Logo"
                      className="w-full h-full object-contain drop-shadow-md"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/Nailed It Logo.png';
                      }}
                    />
                  </div>
                  
                  {/* Floating Micro-Badge */}
                  <div className="absolute -top-2 right-4 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-white text-xs font-bold px-3 py-1 rounded-full shadow-md flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    <span>$30 Flat Rate</span>
                  </div>

                  <div className="absolute -bottom-2 left-4 bg-white border border-[#F472B6] text-[#BE185D] text-xs font-bold px-3 py-1 rounded-full shadow-md flex items-center gap-1">
                    <Heart className="w-3 h-3 fill-[#BE185D]" />
                    <span>100% Hand-Crafted</span>
                  </div>
                </div>

                {/* Card Bottom Description */}
                <div className="mt-6 space-y-2">
                  <h3 className="font-serif text-xl font-bold text-[#292524]">
                    Handcrafted Gel Nail Art
                  </h3>
                  <p className="text-xs text-[#78716C] max-w-xs mx-auto">
                    Every swirl, star, and gold chrome contour is individually painted by Kylee using salon-grade materials.
                  </p>
                </div>

                {/* Custom Order Quick Trigger */}
                <button
                  onClick={onOpenCustomOrderModal}
                  className="mt-4 w-full py-2.5 px-4 rounded-xl bg-[#FAF5EE] hover:bg-[#FEF3C7] text-[#854D0E] font-semibold text-xs border border-[#E7DEC8] hover:border-[#D4AF37] transition-colors flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span>Have a custom theme or leotard to match? Click here!</span>
                </button>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
